<app-page-header title="Slotting" description="Assign products to promotional slot types across the buying calendar."></app-page-header>
<div class="nav_id"><p><u>slotting</u></p></div>
<!-- stepPosition="center" is deliberate. This target fills the viewport, so there is no
     position that works relative to it: "top" puts the box above the top of the page,
     where the library's negative scrollBy can't reach it, and "bottom" pushes it below
     the fold. "center" pins the box to the middle of the screen instead (position:fixed,
     no arrow), so it always fits. Anchoring the step to a smaller element was the other
     way out, but the backdrop cuts out only the target — a small target dims the whole
     board, which is exactly what the tour wants you looking at. -->
<div class="slotting-board-container"
  joyrideStep="slot-cell" stepPosition="center"
  [title]="'Slot your products'"
  [text]="'Click a cell under a week/month column for a product and choose a slot type to assign it a promotional slot.'">

  <dx-data-grid
    #slottingGrid
    [dataSource]="dataSource"
    [remoteOperations]="false"
    [allowColumnReordering]="true"
    [rowAlternationEnabled]="true"
    [allowColumnResizing]="true"
    [columnAutoWidth]="false"
    [showBorders]="true"
    [hoverStateEnabled]="true"
    [loadPanel]="{ enabled: false }"
    (onEditingStart)="onEditingStart($event)"
    (onEditorPreparing)="onEditorPreparing($event)"
    (onRowUpdating)="onRowUpdating($event)"
    (onContentReady)="onContentReady($event)"
    (onCellHoverChanged)="onCellHoverChanged($event)">

    <dxo-state-storing
      [enabled]="true"
      type="custom"
      [customLoad]="loadState"
      [customSave]="saveState"
      [savingTimeout]="2000">
    </dxo-state-storing>

    <dxo-editing
      mode="batch"
      refreshMode="repaint"
      [allowUpdating]="true"
      [allowDeleting]="false"
      [allowAdding]="false"
      [selectTextOnEditStart]="true"
      [startEditAction]="'click'"
      [useIcons]="false">
    </dxo-editing>

    <dxo-master-detail
      [enabled]="periodMode === 'week'"
      template="promotionGroupDetail">
    </dxo-master-detail>

    <dxo-scrolling mode="virtual" columnRenderingMode="virtual"></dxo-scrolling>
    <dxo-sorting mode="multiple"></dxo-sorting>
    <dxo-filter-panel [visible]="true"></dxo-filter-panel>
    <dxo-header-filter [visible]="true"></dxo-header-filter>
    <dxo-search-panel [visible]="true" [width]="240"></dxo-search-panel>
    <dxo-column-fixing [enabled]="true"></dxo-column-fixing>
    <dxo-group-panel [visible]="true"></dxo-group-panel>
    <dxo-grouping #expand [autoExpandAll]="true"></dxo-grouping>
    <dxo-filter-row [visible]="showFilterRow" [applyFilter]="true"></dxo-filter-row>

    <!-- Fixed columns — monthly board: product-level rows -->
    @if (periodMode === 'month') {
      <dxi-column
        dataField="host_product_group_description"
        caption="Category"
        dataType="string"
        [width]="180"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="buying_group_name"
        caption="Buying Group"
        dataType="string"
        [width]="200"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="supplier_name"
        caption="Supplier"
        dataType="string"
        [width]="180"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="product_code"
        caption="Product Code"
        dataType="string"
        [width]="120"
        [allowEditing]="false"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="product_name"
        caption="Product Name"
        dataType="string"
        [width]="250"
        [allowEditing]="false"
        [fixed]="true">
      </dxi-column>
    }

    <!-- Fixed columns — weekly board: promotion-group rows (match V1 data-grid order) -->
    @if (periodMode === 'week') {
      <dxi-column
        dataField="product_group_name"
        caption="Category"
        dataType="string"
        [width]="180"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="buying_group_name"
        caption="Buying Group"
        dataType="string"
        [width]="200"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="promotion_group_name"
        caption="Promotion Group"
        dataType="string"
        [minWidth]="180"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>

      <dxi-column
        dataField="supplier_name"
        caption="Supplier"
        dataType="string"
        [width]="180"
        [allowEditing]="false"
        [allowFiltering]="true"
        [fixed]="true">
      </dxi-column>
    }

    <dxi-column
      dataField="isSlotted"
      dataType="boolean"
      [visible]="false"
      [showInColumnChooser]="false"
      [allowFiltering]="true"
      [allowHeaderFiltering]="false"
      [allowSearch]="false">
    </dxi-column>

    <!-- Dynamic columns for months or weeks -->
    @for (column of dynamicColumns; track column.dataField) {
      <dxi-column
        [dataField]="column.dataField"
        [caption]="column.caption"
        [dataType]="column.dataType"
        [width]="60"
        alignment="center"
        [lookup]="column.lookup"
        [allowFiltering]="false"
        [allowHeaderFiltering]="false"
        [allowSorting]="false"
        [showInColumnChooser]="column.showInColumnChooser"
        [setCellValue]="setCellValue"
        [headerCellTemplate]="periodMode === 'week' ? 'weekHeaderTemplate' : null"
        cellTemplate="cellTemplate">
      </dxi-column>
    }

    <!-- Cell template for circular slot display -->
    <div *dxTemplate="let data of 'cellTemplate'">
      @if (data.value) {
        <div
          class="circle"
          [style.background-color]="getSlotTypeColour(data.value)"
          [attr.title]="getSlotTypeName(data.value)">
          <p class="circle-inner">{{data.value}}</p>
        </div>
      }
    </div>

    <!-- Week header template — three coloured bars above the caption indicate events/holidays -->
    <div *dxTemplate="let data of 'weekHeaderTemplate'" class="week-header">
      <div class="box-container">
        <div class="box red" [style.visibility]="hasSystemEvent(data.column.dataField)"></div>
        <div class="box blue" [style.visibility]="hasCustomEvent(data.column.dataField)"></div>
        <div class="box green"></div>
      </div>
      <div class="week-header-caption">{{ data.column.caption }}</div>
    </div>

    <!-- Master-detail template (weekly only) — lists products belonging to the expanded promotion group,
         reusing the same DetailViewComponent that V1's data-grid uses. -->
    <div *dxTemplate="let rowData of 'promotionGroupDetail'" class="promotion-group-detail">
      <div class="master-detail-caption">
        {{ rowData.data.product_group_name }} — {{ rowData.data.promotion_group_name }}
      </div>
      <app-detail-view
        [key]="rowData.data.promotion_group_id"
        [buyingGroup]="rowData.data.buying_group_id">
      </app-detail-view>
    </div>

    <dxo-export [enabled]="true" [fileName]="'slotting-board-months'"></dxo-export>
    <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>

    <!-- Toolbar with navigation and Create Promotion buttons -->
    <dxo-toolbar>
      <dxi-item location="before">
        <div *dxTemplate>
          <dx-button
            [text]="backwardLabel"
            icon="chevronleft"
            type="normal"
            (onClick)="navigateBackward()">
          </dx-button>
        </div>
      </dxi-item>
      <dxi-item location="before">
        <div *dxTemplate>
          <dx-button
            [text]="forwardLabel"
            icon="chevronright"
            type="normal"
            (onClick)="navigateForward()">
          </dx-button>
        </div>
      </dxi-item>
      <dxi-item location="before">
        <div *dxTemplate>
          <dx-tag-box
            [dataSource]="buyingGroups"
            displayExpr="buying_group_name"
            valueExpr="id"
            [value]="selectedBuyingGroupIds"
            [showSelectionControls]="true"
            [searchEnabled]="true"
            applyValueMode="useButtons"
            [width]="280"
            [multiline]="false"
            [maxDisplayedTags]="1"
            [showMultiTagOnly]="false"
            placeholder="Buying groups"
            (onValueChanged)="onBuyingGroupSelectionChanged($event)">
            <dxo-drop-down-options title="Buying Groups"></dxo-drop-down-options>
          </dx-tag-box>
        </div>
      </dxi-item>
      <dxi-item name="groupPanel" location="before"></dxi-item>
      <dxi-item location="after">
        <div *dxTemplate>
          <dx-button class="search-toggle" icon="search" [class.active]="showFilterRow" (onClick)="toggleFilterRow()"></dx-button>
        </div>
      </dxi-item>
      <dxi-item name="columnChooserButton" location="after"></dxi-item>
      <dxi-item location="after">
        <div *dxTemplate>
          <dx-drop-down-button
            [items]="slotFilterItems"
            displayExpr="name"
            keyExpr="id"
            [text]="currentSlotFilter.name"
            [useSelectMode]="true"
            [selectedItem]="currentSlotFilter"
            (onSelectionChanged)="onSlotFilterChanged($event)">
          </dx-drop-down-button>
        </div>
      </dxi-item>
      <dxi-item location="after">
        <div *dxTemplate>
          <div class="joyride-target-inline" joyrideStep="slot-create-promotion" stepPosition="bottom"
            [title]="'Create a promotion'"
            [text]="'Once you\'ve slotted the products you want, click here to create a promotion from them.'">
            <dx-button
              text="Create Promotion"
              icon="add"
              type="default"
              (onClick)="openCreatePromotion()">
            </dx-button>
          </div>
        </div>
      </dxi-item>
    </dxo-toolbar>
  </dx-data-grid>
</div>

<dx-popup
  [(visible)]="createPromotion"
  [hideOnOutsideClick]="true"
  [showTitle]="true"
  title="Create Promotion"
  height="480px"
  width="700px"
  [showCloseButton]="true">
    <app-create-promotion
      [buyingGroups]="buyingGroups"
      [promotionTypes]="promotionTypes"
      [allSlotTypes]="slotTypes"
      [slottingBoardData]="slottingBoardData"
      [periodMode]="periodMode"
      [weekStartIndex]="weekStartIndex"
      (onPromotionCreated)="handlePromotionCreated($event)">
    </app-create-promotion>
</dx-popup>

<dx-tooltip #eventTooltip [target]="eventTooltipTarget">
  <div *dxTemplate="let data of 'content'">
    <div class="event-tooltip" *ngFor="let item of eventTooltipData">
      <div><b>Date:</b> {{ formatEventDate(item.date) }}</div>
      <div><b>Event:</b> {{ item.event_name }}</div>
      <div><b>State:</b> {{ item.region }}</div>
      <hr>
    </div>
  </div>
</dx-tooltip>

<dx-load-panel
    #loadPanel
    shadingColor="rgba(0,0,0,0.4)"
    [(visible)]="loading"
    [showIndicator]="true"
    [showPane]="true"
    [shading]="true"
    [hideOnOutsideClick]="false"
  >
  </dx-load-panel>
